import { Alert, Flex, Loading, Typography } from '@fe-toolkit/web-components/ssr'

import {
  AppointmentEvents,
  AppointmentSelectorModal as _AppointmentSelectorModal,
  ESlots,
  EVisitType,
  OrderDetailActiveAppointments,
  TApi,
  TimeSlotSchema,
  transformedAppointmentResponseSchema,
} from '@giffgaff/broadband-common'

import { TCustomAxiosErrorBody } from '@fe-toolkit/react-query-hooks-toolkit'
import { AxiosError } from 'axios'
import { z } from 'zod'

import React, { useState } from 'react'

import InstallContactInfoCard from '@/components/organisms/InstallContactInfoCard/InstallContactInfoCard'
import InstallExtraInfoCard from '@/components/organisms/InstallExtraInfoCard/InstallExtraInfoCard'

import ErrorAlert from '@/components/atoms/ErrorAlert/ErrorAlert'

import LearnMoreButton from '../LearnMoreButton/LearnMoreButton'

import LearnMoreAboutInstallsModal from '../LearnMoreAboutInstalsModal/LearnMoreAboutInstallsModal'
import { PlanAppointmentLegacyNotes, TManagedVisitType } from '../../utils/propTypeHelpers'

import type { appointmentUpdate } from '../../ChangeInstallDates.container'

// Workaround for TS2589: CreateTypeFromSchema + Zod 4 causes excessively deep type instantiation
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const AppointmentSelectorModal = _AppointmentSelectorModal as React.ComponentType<any>

export type TChangeAppointmentInfoProps = {
  useFetchMoreAvailableAppointments: TApi['useFetchMoreAvailableAppointments']
  planAppointmentNotes?: PlanAppointmentLegacyNotes
  leadTimeClash: boolean
  appointmentIsInThePast: boolean
  isFetching: boolean
  isLoading: boolean
  requestedAppointmentNoLongerAvailable: boolean
  rebookingRequiredBlocked?: boolean
  isUpdatingAppointments: boolean
  isUpdatingAppointmentsError: boolean
  appointmentInfoUpdate: {
    contactFirstName: string
    contactLastName: string
    contactPhoneNumber: string
    selectedNotes: string[]
    requestedCall: boolean
  }
  appointments?: z.infer<typeof transformedAppointmentResponseSchema>
  appointmentEvents?: AppointmentEvents
  groupedActiveAppointments?: { PRE_WORK?: OrderDetailActiveAppointments; IN_HOME?: OrderDetailActiveAppointments } // TODO: this is being deprecated in favour of relevantActiveAppointment
  relevantActiveAppointment: OrderDetailActiveAppointments
  getAppointmentsError: AxiosError<TCustomAxiosErrorBody> | null
  handleCancelAppointmentChange: () => void
  handleAppointmentInfoUpdate: (
    field: 'contactFirstName' | 'contactLastName' | 'contactPhoneNumber',
    value: string,
    destination: TManagedVisitType,
  ) => void
  setSelectedNotes: (field: string, value: string[]) => void
  handleRequestedCallChange: (value: boolean) => void
  handleMainButtonClick: (date: string | undefined, timeSlot: TimeSlotSchema | undefined) => void
  isFinalTab: boolean
  singleAppointment: boolean
  updatedAppointments: appointmentUpdate[]
}

const ChangeAppointmentInfo = ({
  planAppointmentNotes,
  leadTimeClash,
  appointmentIsInThePast,
  isFetching,
  isLoading,
  requestedAppointmentNoLongerAvailable,
  rebookingRequiredBlocked = false,
  isUpdatingAppointments,
  isUpdatingAppointmentsError,
  appointmentInfoUpdate,
  appointments,
  appointmentEvents,
  groupedActiveAppointments,
  relevantActiveAppointment,
  getAppointmentsError,
  handleCancelAppointmentChange,
  handleAppointmentInfoUpdate,
  setSelectedNotes,
  handleRequestedCallChange,
  useFetchMoreAvailableAppointments,
  handleMainButtonClick,
  isFinalTab,
  singleAppointment,
  updatedAppointments,
}: TChangeAppointmentInfoProps) => {
  const filteredContent =
    relevantActiveAppointment.visitType === EVisitType.IN_HOME
      ? {
          changeBookingTitle: 'Manage home install',
          learnMoreLinkText: 'Learn more about home install',
          complete: 'Your home install is done',
        }
      : {
          changeBookingTitle: 'Manage outside install',
          learnMoreLinkText: 'Learn more about outside install',
          complete: 'Your outside install is done',
        }

  const isAmendable = relevantActiveAppointment.amendable

  const visitType = relevantActiveAppointment.visitType as EVisitType.IN_HOME | EVisitType.PRE_WORK

  const [modalState, setModalState] = useState<boolean>(false)

  const handleLearnMoreClick = () => {
    setModalState((prev: boolean) => !prev)
  }

  const updatedNotesOnly = () => {
    const { contactFirstName, contactLastName, contactPhoneNumber, selectedNotes, requestedCall } =
      appointmentInfoUpdate

    return !!contactFirstName || !!contactLastName || !!contactPhoneNumber || selectedNotes.length > 0 || requestedCall
  }

  const canSubmit = (selectedDate?: Date, selectedTimeSlot?: TimeSlotSchema) => {
    return !!selectedDate || !!selectedTimeSlot || updatedNotesOnly()
  }

  if (updatedAppointments.length < 1) return <Loading />

  if (
    updatedAppointments.find(appointment => appointment.appointmentId === relevantActiveAppointment.id!) === undefined
  ) {
    return <ErrorAlert errorMessage="there has been a problem loading your appointments" />
  }

  const preselectedAppointment: { date: Date; slot: ESlots } = {
    date: new Date(
      updatedAppointments.find(appointment => appointment.appointmentId === relevantActiveAppointment.id!)!.date || '',
    ),
    slot: updatedAppointments.find(appointment => appointment.appointmentId === relevantActiveAppointment.id!)!.slot
      .slot as ESlots,
  }

  const updatedAppointmentExists =
    updatedAppointments &&
    !!updatedAppointments?.find(appointment => appointment.appointmentId === relevantActiveAppointment.id!)?.date &&
    !!updatedAppointments?.find(appointment => appointment.appointmentId === relevantActiveAppointment.id!)?.slot.slot

  const legacyNotes =
    [
      planAppointmentNotes?.healthAndSafety ? `Health and safety: ${planAppointmentNotes.healthAndSafety}` : '',
      planAppointmentNotes?.engineerInstructions ? `Engineer notes: ${planAppointmentNotes.engineerInstructions}` : '',
    ]
      .filter(Boolean)
      .join(', ') || undefined

  return (
    <>
      {!singleAppointment && (
        <Typography as="h3" sx={({ theme }) => ({ marginBottom: theme.spacing.small })} variant="speak">
          {filteredContent.changeBookingTitle}
        </Typography>
      )}
      {/* learn more button */}
      {/* learn more is different in each visit type, but it is handled */}
      <LearnMoreButton
        linkText={filteredContent.learnMoreLinkText}
        visitType={relevantActiveAppointment.visitType as EVisitType}
        onLearnMoreClick={handleLearnMoreClick}
      />
      {isAmendable && (
        <>
          {leadTimeClash && (
            <Alert
              isMinor
              borderRadius="small"
              sx={({ theme }) => ({ margin: `${theme.spacing.small} 0` })}
              variant="error"
            >
              <Typography as="p" variant="speakUp">
                You’ll need to choose a new home install date
              </Typography>
              {/* this is the last instance of groupedActiveAppointments */}
              <Typography as="p" variant="speak">
                Our team needs {groupedActiveAppointments?.PRE_WORK?.inHomeLeadTimeInDays} working days between
                installs.
              </Typography>
            </Alert>
          )}

          {requestedAppointmentNoLongerAvailable && (
            <Alert
              isMinor
              borderRadius="small"
              sx={({ theme }) => ({ margin: `${theme.spacing.small} 0` })}
              variant="error"
            >
              <Typography as="p" variant="speakUp">
                This appointment is no longer available. Please select another time.
              </Typography>
            </Alert>
          )}
          {updatedAppointmentExists && (
            <AppointmentSelectorModal
              appointmentId={relevantActiveAppointment.id}
              canSubmit={canSubmit}
              customSubmitButtonText={isFinalTab ? 'Confirm Install Date' : 'Next'}
              data={appointments}
              events={appointmentEvents}
              getAppointmentsError={getAppointmentsError}
              groupId={relevantActiveAppointment.id}
              handleCloseModal={handleCancelAppointmentChange} // this is badly named, here the 'cancel button' is used to navigate back to the manage order page
              handleConfirmInstallDate={async (date: string | undefined, timeSlot: TimeSlotSchema | undefined) => {
                handleMainButtonClick(date, timeSlot)
              }}
              isGetAppointmentsError={!!getAppointmentsError?.message}
              isLoading={isLoading || isFetching}
              isUpdateAppointmentError={isUpdatingAppointmentsError}
              isUpdatingAppointment={isUpdatingAppointments}
              preselectedAppointment={preselectedAppointment}
              updateAppointmentError={null}
              useFetchMoreAvailableAppointments={useFetchMoreAvailableAppointments}
            >
              <Flex
                sx={({ theme }) => ({
                  margin: `${theme.spacing.small} 0`,
                  flexDirection: 'column',
                  gap: theme.spacing.medium,
                })}
              >
                <InstallContactInfoCard
                  firstName={appointmentInfoUpdate.contactFirstName}
                  handleFirstNameChange={value => handleAppointmentInfoUpdate('contactFirstName', value, visitType)}
                  handleLastNameChange={value => handleAppointmentInfoUpdate('contactLastName', value, visitType)}
                  handlePhoneNumberChange={value => handleAppointmentInfoUpdate('contactPhoneNumber', value, visitType)}
                  handleRequestedCallChange={handleRequestedCallChange}
                  lastName={appointmentInfoUpdate.contactLastName}
                  phoneNumber={appointmentInfoUpdate.contactPhoneNumber}
                  requestedCall={appointmentInfoUpdate.requestedCall}
                  variant={relevantActiveAppointment.visitType as TManagedVisitType}
                />
                <InstallExtraInfoCard
                  legacyNotes={legacyNotes}
                  selectedNotes={appointmentInfoUpdate.selectedNotes}
                  setSelectedNotes={setSelectedNotes}
                  variant={relevantActiveAppointment.visitType as TManagedVisitType}
                />
              </Flex>
              {isUpdatingAppointmentsError && isFinalTab && (
                <Alert isMinor borderRadius="small" variant="error">
                  <Typography as="p" variant="speakUp">
                    Oops. Something went wrong
                  </Typography>
                </Alert>
              )}
              {rebookingRequiredBlocked && isFinalTab && (
                <Alert isMinor borderRadius="small" variant="error">
                  <Typography as="p" variant="speakUp">
                    You need to rebook your other appointment too before you can continue.
                  </Typography>
                </Alert>
              )}
            </AppointmentSelectorModal>
          )}
        </>
      )}
      {!isAmendable && !appointmentIsInThePast && (
        // is it because the date has passed -
        <Alert isMinor borderRadius="small" variant="info">
          <Typography as="span" variant="speakUp">
            You're unable to change your appointment right now.
          </Typography>
          <Typography as="p" variant="speak">
            We're currently getting the order approved by our friends Virgin Media. Check back soon, once the order is
            given the OK, you'll be able to change your appointment
          </Typography>
        </Alert>
      )}
      {!isAmendable && appointmentIsInThePast && (
        <Alert isMinor borderRadius="small" variant="success">
          {filteredContent.complete}
        </Alert>
      )}
      <LearnMoreAboutInstallsModal
        closeModal={() => setModalState(false)}
        visitType={modalState ? (relevantActiveAppointment.visitType as EVisitType) : null}
      />
    </>
  )
}

export default ChangeAppointmentInfo
